import React, {useContext} from "react";
import {Button, Tag} from "antd";
import '../../../../styles/component/employee-details/education-tab.scss';
import {Award, BookCopy, GraduationCap} from "lucide-react";
import SectionCard from "../common/SectionCard";
import DetailGrid from "../common/DetailGrid";
import {EditOutlined} from "@ant-design/icons";
import {useLocation, useNavigate} from "react-router-dom";
import UserLoginContext from "../../../../context/userLoginContext";
import Locale from "../../../../core/locale/locale";
import dayjs from "dayjs";
import EmptyData from "../../../common/empty-data/empty-data";

const EducationTab = ({employeeData}: any) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {user} = useContext(UserLoginContext);
    const isActiveEmployee = location.state?.isActive ?? true;
    const qualification = employeeData?.qualification;
    if (!qualification) {
        return (
            <EmptyData
                icon={<BookCopy className={'education-empty-data-icon'}/>}
                title={"No Education Details Available"}
                subtitle={"Please provide education information to complete the profile"}
            />
        )
    }
    const navigateToQualificationDetail = () => navigate(`/employee/edit/qualification`, {state: {empId: employeeData.id}});

    return (
        <div className="education-tab">
            <div className="education-grid">

                {qualification?.matriculation && (
                    <SectionCard
                        title={"Matriculation (10th)"}
                        subHeading={qualification.matriculation.boardOrUniversity}
                        icon={<GraduationCap
                            className={'employee-detail-lucide graduation-matriculation-cap-icon'}/>}
                        extra={
                            user?.isAdmin && isActiveEmployee && (
                                <Button type={"link"} onClick={() => navigateToQualificationDetail()}> <EditOutlined/>
                                </Button>
                            )
                        }
                    >
                        <DetailGrid
                            data={[
                                {label: "Passing Year", value: qualification.matriculation.passingYear},
                                {label: "Percentage", value: qualification.matriculation.percentage},
                            ]}
                        />
                    </SectionCard>
                )}

                {qualification?.seniorSecondary && (
                    <SectionCard
                        title={"Senior Secondary (12th)"}
                        subHeading={qualification.seniorSecondary.boardOrUniversity}
                        icon={<GraduationCap
                            className={'employee-detail-lucide graduation-seniorSecondary-cap-icon'}/>}
                        extra={
                            user?.isAdmin && isActiveEmployee && (
                                <Button type={"link"} onClick={() => navigateToQualificationDetail()}> <EditOutlined/>
                                </Button>
                            )
                        }
                    >
                        <DetailGrid
                            data={[
                                {label: "Passing Year", value: qualification.seniorSecondary.passingYear},
                                {label: "Percentage", value: qualification.seniorSecondary.percentage},
                            ]}
                        />
                    </SectionCard>
                )}

                {qualification?.graduation?.length > 0 &&
                qualification.graduation.map((graduation: any, index: number) => (
                    <SectionCard
                        key={index}
                        title={`Graduation (${graduation.qualificationItem?.code ? graduation.qualificationItem?.code : '-'} )`}
                        subHeading={graduation.boardOrUniversity}
                        icon={<GraduationCap className={'employee-detail-lucide graduation-cap-icon'}/>}
                        extra={
                            user?.isAdmin && isActiveEmployee && (
                                <Button type={"link"} onClick={() => navigateToQualificationDetail()}> <EditOutlined/>
                                </Button>
                            )
                        }
                    >
                        <DetailGrid
                            data={[
                                {label: "Passing Year", value: graduation.passingYear},
                                {label: "CGPA", value: graduation.cgpa},
                            ]}
                        />
                    </SectionCard>
                ))}

                {qualification?.postGraduation?.length > 0 &&
                qualification.postGraduation.map((postGraduation: any, index: number) => (
                    <SectionCard
                        key={index}
                        title={`Post Graduation (${postGraduation.qualificationItem?.code ? postGraduation.qualificationItem?.code : '-'} )`}
                        subHeading={postGraduation.boardOrUniversity}
                        icon={<GraduationCap className={'employee-detail-lucide postGraduation-cap-icon'}/>}
                        extra={
                            user?.isAdmin && isActiveEmployee && (
                                <Button type={"link"} onClick={() => navigateToQualificationDetail()}> <EditOutlined/>
                                </Button>
                            )
                        }
                    >
                        <DetailGrid
                            data={[
                                {label: "Passing Year", value: postGraduation.passingYear},
                                {label: "CGPA", value: postGraduation.cgpa},
                            ]}
                        />
                    </SectionCard>
                ))}
            </div>

            {qualification?.certifications?.some(
                (cert: any) => cert?.title || cert?.university || cert?.certifiedBy
            ) && (
                <div className="certification-section">
                    <div className="certification-header">
                        <div className={'icon-and-certification'}>
                            <div className="icon-box orange">
                                <Award/>
                            </div>
                            <h3>Certifications</h3>
                        </div>
                        <div>
                            {
                                user?.isAdmin && isActiveEmployee && (
                                    <Button
                                        type={"link"}
                                        className={"edit-icon"}
                                        onClick={() => navigateToQualificationDetail()}> <EditOutlined/>
                                    </Button>
                                )
                            }
                        </div>

                    </div>

                    {qualification.certifications
                        .map((cert: any, index: number) => (
                            <div key={index} className="cert-card">
                                <div className="cert-top">
                                    <div className="cert-title">
                                        <div className="icon-box yellow">
                                            <Award/>
                                        </div>
                                        <div>
                                            <h4>{cert.title}</h4>
                                            <p>
                                                {cert.university} • Certified by: {cert.certifiedBy}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="cert-details">
                                    <div>
                                        <span>CERTIFIED ON</span>
                                        <p>{cert.certifiedOn ? dayjs(cert.certifiedOn).format(Locale.displayDate) : "-"}</p>
                                    </div>
                                    {cert.skills?.length > 0 && (
                                        <div className="education-skills">
                                            <span>SKILLS</span>
                                            <div className="skill-tags">
                                                {cert.skills.map((skill: string, i: number) => (
                                                    <Tag key={i}>{skill}</Tag>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            )}

        </div>
    );

};

export default EducationTab;
